import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './AsideElementAi.module.scss';

import { useChatStorage } from './ai/hooks/useChatStorage';
import { useFileUpload } from './ai/hooks/useFileUpload';
import { useMessageHandler } from './ai/hooks/useMessageHandler';

import MessageBubble from './ai/MessageBubble';
import FilePreview from './ai/FilePreview';
import ChatInput from './ai/ChatInput';
import EmptyState from './ai/EmptyState';
import LoadingIndicator from './ai/LoadingIndicator';

const AsideElementAi = ({ onAiResponse }) => {
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    messages,
    setMessages,
    message,
    setMessage,
    attachedFiles,
    setAttachedFiles,
    clearAllStorage
  } = useChatStorage();

  const { sendMessageToAPI, onSendMessage } = useMessageHandler(
    setIsLoading,
    onAiResponse,
    setMessages
  );

  const {
    isDragging,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop
  } = useFileUpload();

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addFiles = (files) => {
    try {
      const newFiles = Array.from(files).map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type
      }));

      setAttachedFiles(prev => [...prev, ...newFiles]);
    } catch (e) {
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      addFiles(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (fileId) => {
    try {
      setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (e) {
    }
  };

  const handleDropFiles = (e) => {
    const files = handleDrop(e);
    if (files && files.length > 0) {
      addFiles(files);
    }
  };
  
const handleSend = useCallback(async () => {
  if (message.trim() || attachedFiles.length > 0) {
    const currentMessage = message.trim();
    const currentFiles = [...attachedFiles];

    setMessage('');
    setAttachedFiles([]);
    chatInputRef.current?.resetHeight?.();

    setIsLoading(true);

    try {
      const data = await sendMessageToAPI(currentMessage, currentFiles.map(f => f.file));

      const userMessage = data.user;
      const botResponse = data.bot;

      onSendMessage(userMessage, botResponse);

      } catch (e) {
      } finally {
        setIsLoading(false);
      }
    } 
  }, [message, attachedFiles, sendMessageToAPI, onSendMessage, setMessage, setAttachedFiles]);

  const handleDuplicateLastMessage = () => {
    if (messages.length > 0) {
      const lastUserMessage = messages.slice().reverse().find(msg => msg.type === 'user');
      if (lastUserMessage) {
        setMessage(lastUserMessage.text);
        if (chatInputRef.current?.focus) {
          chatInputRef.current.focus();
        }
      }
    }
  };

  const handleClearAllMessages = () => {
    if (window.confirm("Are you sure you want to delete all messages?")) {
      setMessages([]);
      clearAllStorage();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.messagesArea}>
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))
        )}
     
        {isLoading && <LoadingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      <div className={styles.inputContainer}>
        <FilePreview 
          attachedFiles={attachedFiles} 
          onRemoveFile={removeFile} 
        />

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className={styles.fileInput}
          accept="
            image/jpeg,
            image/jpg,
            image/png,
            image/gif,
            image/webp,
            application/pdf,
            text/plain
          "
          multiple
          style={{ display: 'none' }}
        />

        <ChatInput
          ref={chatInputRef}
          message={message}
          setMessage={setMessage}
          onSend={handleSend}
          onFileClick={handleFileClick}
          onDuplicateLastMessage={handleDuplicateLastMessage}
          onClearAllMessages={handleClearAllMessages}
          isDragging={isDragging}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDropFiles}
          hasMessages={messages.length > 0}
          attachedFilesCount={attachedFiles.length}
        />
      </div>
    </div>
  );
};

AsideElementAi.propTypes = {
  onAiResponse: PropTypes.func
};

AsideElementAi.defaultProps = {
  onAiResponse: null
};

export default AsideElementAi;